import { useCallback, useEffect } from 'react';
import useDebounceFunction from '../../../../../../components/Hooks/useDebouncedFunction';
import LangEnum from '../../../../../../lang/LangEnum';
import snackbarStore from '../../../../../../stores/Snackbar.store';
import { useTranslation } from '../../../../../translation/TranslationContext';
import { useChatSocketContext } from '../../../chatSocket/hooks/useChatSocketContext';
import { errorConnectionToWs, updateSelectedChat } from '../../reducer/chatReducer.actions';
import { WsErrorHandler } from '../../wsErrorHandler/wsErrorHandler';
import { UseHandleWithSocketStateParams } from './useHandleWithSocketState.types';

const useHandleWithSocketState = ({
  state,
  dispatch,
  chatsListState,
}: UseHandleWithSocketStateParams) => {
  const { translate } = useTranslation();

  const chatSocket = useChatSocketContext();

  const handleErrorConnectingToWsChat = useCallback((notifyUser?: boolean) => {
    if (notifyUser) {
      snackbarStore.handleMessage({
        message: translate(LangEnum.ERROR_CONNECTING_TO_WS_CHAT),
        type: 'error',
      });
    }

    dispatch(errorConnectionToWs());
  }, []);

  const { handleDebounceFunction: handleDebounceFetchChatFromWs } = useDebounceFunction();
  const handleFetchChatsFromWs = useCallback(async () => {
    const promisesToSolve = chatSocket.chatIdsToFetch.map((chatId) =>
      chatsListState.handleReactivateChatOrAddNewChatById(chatId),
    );

    await Promise.allSettled(promisesToSolve);

    chatSocket.chatIdsToFetch.forEach((chatId) => chatSocket.removeChatToFetchById(chatId));
  }, [chatSocket, chatsListState]);

  const { handleDebounceFunction: handleDebounceRemoveChatFromWs } = useDebounceFunction();
  const handleRemoveChatsFromWs = async () => {
    chatSocket.chatIdsToRemove.forEach((chatId) => {
      chatsListState.moveChatToTop(chatId, true);

      if (state.activeChat?.id === chatId) {
        dispatch(updateSelectedChat());
      }
    });

    chatSocket.chatIdsToRemove.forEach((chatId) => chatSocket.removeChatToRemoveById(chatId));
  };

  useEffect(() => {
    if (!chatSocket.lastWebsocketMessage) {
      return;
    }

    chatsListState.moveChatToTop(chatSocket.lastWebsocketMessage.chatId, false);
  }, [chatSocket.lastWebsocketMessage]);

  useEffect(() => {
    if (chatSocket.receivedNotifications.length > 0) {
      chatsListState.handleUpdateChatsWithNotifications(chatSocket.receivedNotifications);
      chatSocket.handleRemoveNotificationsByMessageIds(
        chatSocket.receivedNotifications.map((notification) => notification.messageId),
      );
    }
  }, [chatSocket.receivedNotifications]);

  useEffect(() => {
    handleDebounceFetchChatFromWs(handleFetchChatsFromWs);
  }, [chatSocket.chatIdsToFetch]);

  useEffect(() => {
    handleDebounceRemoveChatFromWs(handleRemoveChatsFromWs);
  }, [chatSocket.chatIdsToRemove]);

  useEffect(() => {
    if (chatSocket.wsException) {
      const wsErrorHandler = new WsErrorHandler(translate);
      wsErrorHandler.handle(chatSocket.wsException);
    }
  }, [chatSocket.wsException]);

  useEffect(() => {
    if (chatSocket.errorConnectingToWsChat.hasError) {
      handleErrorConnectingToWsChat(chatSocket.errorConnectingToWsChat.notifyUser);
    }
  }, [chatSocket.errorConnectingToWsChat, handleErrorConnectingToWsChat]);
};

export default useHandleWithSocketState;
